import React, { useEffect, useState } from 'react';
import { ArrowRight, CalendarDays, Check, CheckCircle2, Gauge, Headphones, Loader2, ReceiptText, ShieldCheck, Users } from 'lucide-react';
import { ApiService } from '../services/api';
import { BETA_ANNUAL_DISCOUNT_PERCENT, BillingCycle, formatCurrency, getBetaAnnualSavings, getPlanConfig, getStandardAnnualFee, getVisiblePlans, normalizePlanId } from '../config/pricing';
import { useToast } from '../context/ToastContext';
import { useSubscription } from '../context/SubscriptionContext';
import { SubscriptionTier } from '../types';

interface UsageSummary {
  since: string;
  total: number;
  byType: Record<string, number>;
  unbilled: number;
}

export const BillingDashboard: React.FC = () => {
  const { tier } = useSubscription();
  const [ticketsProcessed, setTicketsProcessed] = useState(0);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [loadingPlan, setLoadingPlan] = useState<SubscriptionTier | null>(null);
  const { addToast } = useToast();

  useEffect(() => {
    loadBillingData();
  }, []);

  const getBillingPeriodBounds = () => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
    return { start: start.toISOString(), end: end.toISOString() };
  };

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const [dashboard, summary] = await Promise.all([
        ApiService.getDashboardData('month'),
        ApiService.getUsageSummary('month').catch(() => null),
      ]);

      setTicketsProcessed(dashboard?.totalTickets || 0);
      setUsageSummary(summary);
    } catch (error) {
      console.error('Failed to load billing data:', error);
      addToast('Failed to load billing information', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  const normalizedTier = tier === 'trial' ? 'trial' : normalizePlanId(tier);
  const plan = getPlanConfig(tier);
  const plans = getVisiblePlans();
  const fixedFee = plan.monthlyFee;
  const { start: periodStart, end: periodEnd } = getBillingPeriodBounds();
  const daysUntilBilling = Math.ceil((new Date(periodEnd).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  const activePlanId = tier === 'trial' ? 'trial' : normalizePlanId(tier);
  const planHighlights: Record<string, { label: string; items: string[] }> = {
    starter: {
      label: 'Core FleetMode included',
      items: ['Unlimited PCNs', 'All five AI assistants', 'Per-PCN AI summary'],
    },
    growth: {
      label: 'Everything in Starter, plus',
      items: ['Monthly aggregate report', 'Repeat driver/location flags', 'Recovery tracking', 'Hire agreement storage'],
    },
    pro_fleet: {
      label: 'Everything in Growth, plus',
      items: ['Evidence packs', 'Legal-ready PDF export', 'Audit trail', 'Role-based access', 'Bulk PCN actions'],
    },
    concierge: {
      label: 'Everything in Pro Fleet, plus',
      items: ['Multi-branch controls', 'Dedicated account contact', 'Managed onboarding', 'Custom SLA'],
    },
  };

  const handlePlanChange = async (planId: SubscriptionTier) => {
    const normalized = normalizePlanId(planId);

    if (activePlanId === normalized) {
      addToast('You are already on this plan.', 'info');
      return;
    }

    void ApiService.recordFleetModeLeadEvent('plan_selected', 'billing', {
      selected_plan: normalized,
      active_plan: activePlanId,
      billing_cycle: billingCycle,
    }).catch(() => {
      // Internal signal tracking must not block checkout.
    });

    if (normalized === 'concierge') {
      void ApiService.recordFleetModeLeadEvent('upgrade_clicked', 'billing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
        channel: 'mailto',
      }).catch(() => {});
      window.location.href = 'mailto:support@pcnguard.co.uk?subject=FleetMode%20Concierge%20plan%20review';
      return;
    }

    setLoadingPlan(normalized);
    try {
      await ApiService.recordFleetModeLeadEvent('upgrade_clicked', 'billing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
        channel: 'stripe',
      }).catch(() => {});
      const { url } = await ApiService.createCheckoutSession(normalized, billingCycle);
      if (!url) throw new Error('Missing checkout URL');
      await ApiService.recordFleetModeLeadEvent('payment_started', 'billing', {
        selected_plan: normalized,
        active_plan: activePlanId,
        billing_cycle: billingCycle,
      }).catch(() => {});
      addToast('Opening secure Stripe Checkout...', 'info');
      window.location.href = url;
    } catch (error) {
      console.error(error);
      addToast('Could not start checkout. Please try again.', 'error');
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-primary-700 dark:border-primary-900/60 dark:bg-primary-950/30 dark:text-primary-300">
              <ShieldCheck size={14} />
              {normalizedTier === 'trial' ? 'Trial workspace' : 'FleetMode plan'}
            </div>
            <h2 className="mt-4 text-2xl font-bold text-slate-900 dark:text-white">{normalizedTier === 'trial' ? '3-PCN AI Control Trial' : plan.name}</h2>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              Paid FleetMode plans include unlimited PCNs. This page shows your subscription, processed PCNs, and workflow activity.
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-right dark:border-slate-800 dark:bg-slate-950/40">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">Monthly subscription</p>
            <p className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">
              {fixedFee === null ? 'Custom' : formatCurrency(fixedFee)}
            </p>
            {fixedFee !== null && <p className="text-sm text-slate-500 dark:text-slate-400">per month</p>}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <ReceiptText className="h-5 w-5 text-primary-600" />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">PCNs managed this month</p>
          <p className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">{ticketsProcessed}</p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">A processed PCN means AI extraction completed and key fields were confirmed or accepted.</p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <Gauge className="h-5 w-5 text-primary-600" />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">{normalizedTier === 'trial' ? 'Trial capacity' : 'Plan capacity'}</p>
          <p className="mt-1 text-3xl font-bold text-slate-900 dark:text-white">
            {normalizedTier === 'trial' ? '3 PCNs' : 'Unlimited PCNs'}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            {normalizedTier === 'trial' ? 'Your trial ends after 3 processed PCNs or 7 days, whichever comes first.' : `${plan.seats} included on this plan.`}
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <CalendarDays className="h-5 w-5 text-primary-600" />
          <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">Billing period</p>
          <p className="mt-1 text-lg font-bold text-slate-900 dark:text-white">
            {new Date(periodStart).toLocaleDateString('en-GB')} - {new Date(periodEnd).toLocaleDateString('en-GB')}
          </p>
          <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">Next billing date in {daysUntilBilling} {daysUntilBilling === 1 ? 'day' : 'days'}.</p>
        </div>
      </div>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">Plans and upgrades</h2>
            <p className="mt-1 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Manage the current subscription and compare the next best plan from the same billing screen.
            </p>
            <p className="mt-2 text-sm font-semibold text-primary-700 dark:text-primary-300">
              Beta annual lock-in: choose yearly during beta and get {BETA_ANNUAL_DISCOUNT_PERCENT}% off your first year.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-950/40">
            {(['monthly', 'yearly'] as BillingCycle[]).map((cycle) => (
              <button
                key={cycle}
                onClick={() => setBillingCycle(cycle)}
                className={`rounded-md px-4 py-2 text-sm font-semibold capitalize transition ${
                  billingCycle === cycle
                    ? 'bg-slate-950 text-white shadow-sm dark:bg-white dark:text-slate-950'
                    : 'text-slate-600 hover:text-slate-950 dark:text-slate-300 dark:hover:text-white'
                }`}
              >
                {cycle}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 grid gap-4 lg:grid-cols-2 2xl:grid-cols-4">
          {plans.map((candidate) => {
            const isCurrent = activePlanId === candidate.id;
            const price = billingCycle === 'monthly' ? candidate.monthlyFee : candidate.yearlyFee;
            const standardAnnualFee = getStandardAnnualFee(candidate.id);
            const annualSavings = getBetaAnnualSavings(candidate.id);
            const isLoading = loadingPlan === candidate.id;
            const cardTone = isCurrent
              ? 'border-emerald-400 bg-slate-950 text-white shadow-lg shadow-emerald-950/20 ring-1 ring-emerald-300/40'
              : candidate.id === 'concierge'
                ? 'border-slate-900 bg-slate-950 text-white dark:border-slate-600 dark:bg-slate-900 dark:shadow-black/20'
                : 'border-slate-200 bg-white text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:shadow-black/20';
            const mutedText = isCurrent || candidate.id === 'concierge' ? 'text-slate-300' : 'text-slate-600 dark:text-slate-300';
            const softText = isCurrent || candidate.id === 'concierge' ? 'text-slate-400' : 'text-slate-500 dark:text-slate-400';
            const strongText = isCurrent || candidate.id === 'concierge' ? 'text-white' : 'text-slate-950 dark:text-white';
            const panelTone = isCurrent || candidate.id === 'concierge'
              ? 'border-white/10 bg-white/[0.06]'
              : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/40';

            return (
              <article
                key={candidate.id}
                className={`flex min-h-[360px] flex-col rounded-lg border p-5 shadow-sm ${cardTone}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className={`text-lg font-bold ${strongText}`}>{candidate.shortName}</h3>
                    <p className={`mt-2 text-sm leading-6 ${mutedText}`}>{candidate.description}</p>
                  </div>
                  {isCurrent && (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-800">
                      <CheckCircle2 size={13} />
                      Active
                    </span>
                  )}
                </div>

                <div className="mt-4">
                  <p className={`text-2xl font-bold ${strongText}`}>
                    {price === null ? 'Custom' : formatCurrency(price)}
                    {price !== null && <span className={`ml-1 text-xs font-semibold ${softText}`}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>}
                  </p>
                  <div className={`mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold ${isCurrent || candidate.id === 'concierge' ? 'text-slate-200' : 'text-slate-700 dark:text-slate-200'}`}>
                    <Users size={14} />
                    <span>{candidate.seats}</span>
                    <span className={softText}>·</span>
                    <span>Unlimited PCNs</span>
                    <span className={softText}>·</span>
                    <span>All AI assistants</span>
                  </div>
                </div>

                {price !== null && billingCycle === 'yearly' && annualSavings !== null && standardAnnualFee !== null && (
                  <div className="mt-4 rounded-lg border border-slate-700 bg-slate-950 p-3 text-white shadow-sm">
                    <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-300">
                      Beta annual lock-in
                    </div>
                    <div className="mt-1 text-sm font-bold text-white">
                      Save {formatCurrency(annualSavings)} in year 1
                    </div>
                    <div className="mt-1 text-[11px] leading-5 text-slate-300">
                      Normally <span className="line-through">{formatCurrency(standardAnnualFee)}/yr</span> with {BETA_ANNUAL_DISCOUNT_PERCENT}% beta discount applied.
                    </div>
                  </div>
                )}

                <div className={`mt-4 rounded-lg border p-3 ${panelTone}`}>
                  <div className={`text-xs font-bold uppercase tracking-wide ${softText}`}>
                    {planHighlights[candidate.id].label}
                  </div>
                  <ul className="mt-3 space-y-2">
                    {planHighlights[candidate.id].items.map((item) => (
                    <li key={item} className={`flex items-start gap-2 text-sm ${isCurrent || candidate.id === 'concierge' ? 'text-slate-200' : 'text-slate-700 dark:text-slate-300'}`}>
                      <Check className={`mt-0.5 h-4 w-4 shrink-0 ${isCurrent ? 'text-emerald-300' : candidate.id === 'concierge' ? 'text-amber-300' : 'text-primary-600'}`} />
                      {item}
                    </li>
                  ))}
                  </ul>
                </div>

                <button
                  onClick={() => handlePlanChange(candidate.id)}
                  disabled={isCurrent || isLoading}
                  className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-bold transition ${
                    isCurrent
                      ? 'cursor-default bg-slate-800 text-slate-300 ring-1 ring-white/10'
                      : candidate.id === 'concierge'
                        ? 'bg-amber-300 text-slate-950 hover:bg-amber-200'
                        : 'bg-slate-950 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200'
                  }`}
                >
                  {isLoading ? <Loader2 size={16} className="animate-spin" /> : candidate.id === 'concierge' ? <Headphones size={16} /> : <ArrowRight size={16} />}
                  {isCurrent ? 'Current plan' : candidate.id === 'concierge' ? 'Book review' : 'Choose plan'}
                </button>
              </article>
            );
          })}
        </div>
      </section>

      {!!usageSummary && (
        <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <h3 className="text-lg font-bold text-slate-900 dark:text-white">Internal AI and workflow telemetry</h3>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Since {new Date(usageSummary.since).toLocaleDateString('en-GB')}. These records support audit, abuse prevention, and AI cost monitoring.
          </p>
          <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              ['Ticket created', usageSummary.byType?.ticket_created ?? 0],
              ['Ticket uploaded', usageSummary.byType?.ticket_uploaded ?? 0],
              ['AI extraction', usageSummary.byType?.ai_extraction_used ?? 0],
              ['Appeal draft', usageSummary.byType?.appeal_generated ?? 0],
            ].map(([label, value]) => (
              <div key={String(label)} className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/40">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
                <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <button
        className="rounded-lg border border-red-200 bg-white px-4 py-2 font-medium text-red-700 transition-colors hover:bg-red-50 dark:border-red-900/50 dark:bg-slate-900 dark:text-red-300 dark:hover:bg-red-900/10"
        onClick={() => addToast('Cancellation workflow coming soon', 'info')}
      >
        Cancel subscription
      </button>
    </div>
  );
};
